// Narrative client-story block — editorial layout, no nested cards.
// Cognitive levers: identifiable-victim effect, narrative transportation, anchoring (owed → settled).
// SEO/AEO-safe: real semantic headings, factual outcomes, disclaimer about anonymization.

import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { ClientStory } from "@/data/site/clientStories";

interface Props {
  story: ClientStory;
  eyebrow?: string;
  variant?: "light" | "navy";
}

export const ClientStoryBlock = ({ story, eyebrow = "A real client case", variant = "light" }: Props) => {
  const isNavy = variant === "navy";
  return (
    <section
      aria-labelledby={`story-${story.id}`}
      className={isNavy ? "bg-gradient-navy text-white py-20 lg:py-28" : "bg-secondary py-20 lg:py-28"}
    >
      <div className="container mx-auto px-4 max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
          {eyebrow}
        </p>
        <h2
          id={`story-${story.id}`}
          className={`font-heading text-3xl lg:text-[40px] font-bold leading-[1.15] mb-3 [text-wrap:balance] ${
            isNavy ? "" : "text-navy"
          }`}
        >
          "{story.hook}"
        </h2>
        <p className={`text-sm mb-10 ${isNavy ? "text-white/60" : "text-muted-foreground"}`}>
          {story.name} · {story.occupation} · {story.location}
        </p>

        <div className={`space-y-5 text-[17px] leading-[1.7] border-l-2 pl-6 ${
          isNavy ? "border-gold/40 text-white/85" : "border-gold/60 text-foreground/85"
        }`}>
          {story.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-10 pt-8 border-t border-current/15">
          <p className={`text-xs font-bold uppercase tracking-[0.15em] mb-3 ${
            isNavy ? "text-gold" : "text-gold"
          }`}>
            What we did
          </p>
          <p className={`mb-8 ${isNavy ? "text-white/90" : "text-navy"}`}>{story.whatWeDid}</p>

          <dl className="grid sm:grid-cols-3 gap-x-8 gap-y-5">
            <Outcome label="Owed before" value={story.outcome.owedBefore} isNavy={isNavy} />
            <Outcome label="Resolved for" value={story.outcome.settledFor} isNavy={isNavy} highlight />
            <Outcome label="Timeline" value={story.outcome.timeframe} isNavy={isNavy} />
          </dl>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className={`text-xs ${isNavy ? "text-white/80" : "text-muted-foreground"}`}>
            Client name and identifying details changed for privacy. Outcomes vary by case.
          </p>
          <Link
            to={story.serviceHref}
            className={`inline-flex items-center gap-1 text-sm font-semibold flex-shrink-0 ${
              isNavy ? "text-gold hover:text-gold-light" : "text-navy hover:text-gold"
            }`}
          >
            See this service <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Outcome = ({
  label,
  value,
  isNavy,
  highlight,
}: {
  label: string;
  value: string;
  isNavy: boolean;
  highlight?: boolean;
}) => (
  <div>
    <dt className={`text-[11px] font-bold uppercase tracking-[0.15em] mb-1 ${
      isNavy ? "text-white/80" : "text-muted-foreground"
    }`}>
      {label}
    </dt>
    <dd
      className={`font-heading font-bold text-xl lg:text-2xl ${
        highlight ? "text-gold" : isNavy ? "text-white" : "text-navy"
      }`}
    >
      {value}
    </dd>
  </div>
);
